/**
 * Admin API client – moderator management (ADMIN only).
 */

import apiClient from './client';
import type {
  ModeratorResponse,
  AddModeratorRequest,
  AdministratorInfo,
  AddAdministratorRequest,
  AddAdministratorResponse,
  AdminUsersList,
} from '../types/admin';
import type { PlatformStats } from '../types/platformStats';

/**
 * List all moderators.
 */
export const getModerators = async (): Promise<ModeratorResponse[]> => {
  const response = await apiClient.get<ModeratorResponse[]>('/admin/moderators');
  return response.data;
};

/**
 * Add a user as moderator by userId or username.
 */
export const addModerator = async (
  request: AddModeratorRequest
): Promise<ModeratorResponse> => {
  const body: { userId?: string; username?: string } = {};
  if (request.userId) body.userId = request.userId;
  if (request.username) body.username = request.username;
  const response = await apiClient.post<ModeratorResponse>(
    '/admin/moderators',
    body
  );
  return response.data;
};

/**
 * Remove a user from the moderator list.
 */
export const removeModerator = async (userId: string): Promise<void> => {
  await apiClient.delete(`/admin/moderators/${userId}`);
};

/**
 * List all users with the ADMIN realm role (from Keycloak).
 */
export const getAdministrators = async (): Promise<AdministratorInfo[]> => {
  const response = await apiClient.get<AdministratorInfo[]>('/admin/administrators');
  return response.data;
};

/**
 * Add a user as administrator (assigns ADMIN realm role in Keycloak).
 */
export const addAdministrator = async (
  request: AddAdministratorRequest
): Promise<AddAdministratorResponse> => {
  const response = await apiClient.post<AddAdministratorResponse>(
    '/admin/administrators',
    { username: request.username }
  );
  return response.data;
};

/**
 * List registered users from Keycloak.
 */
export const getUsers = async (params?: {
  first?: number;
  max?: number;
  search?: string;
}): Promise<AdminUsersList> => {
  const query: { first?: number; max?: number; search?: string } = { ...params };
  if (!query.search?.trim()) {
    delete query.search;
  } else {
    query.search = query.search.trim();
  }
  const response = await apiClient.get<AdminUsersList>('/admin/users', { params: query });
  return response.data;
};

/**
 * Permanently delete a user and all their content.
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await apiClient.delete(`/admin/users/${userId}`);
};

export const getPlatformStats = async (): Promise<PlatformStats> => {
  const response = await apiClient.get<PlatformStats>('/admin/stats');
  return response.data;
};

export const adminApi = {
  getModerators,
  addModerator,
  removeModerator,
  getAdministrators,
  addAdministrator,
  getUsers,
  deleteUser,
  getPlatformStats,
};
